import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { CreateRideInput, MeetingPointInput, UpdateRideInput } from '@ridemaadi/types';
import type { MeetingPoint, RideDetail } from '@ridemaadi/types';
import { ridesApi } from './api';

export const rideKeys = {
  mine: ['rides', 'mine'] as const,
  public: ['rides', 'public'] as const,
  detail: (id: string) => ['rides', 'detail', id] as const,
};

/** Shared poll interval for joined-ride list (Ride Mode + notifications). */
export const MY_RIDES_POLL_MS = 12_000;

export function useMyRides(options?: { enabled?: boolean; refetchMs?: number }) {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: rideKeys.mine,
    queryFn: () => ridesApi.listMine(),
    enabled,
    refetchInterval: enabled ? (options?.refetchMs ?? MY_RIDES_POLL_MS) : false,
  });
}

export function usePublicRides() {
  return useQuery({ queryKey: rideKeys.public, queryFn: () => ridesApi.listPublic() });
}

export function useRide(id: string | undefined) {
  return useQuery({
    queryKey: rideKeys.detail(id ?? ''),
    queryFn: () => ridesApi.get(id!),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

export function useCreateRide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRideInput) => ridesApi.create(input),
    onSuccess: (ride) => {
      qc.invalidateQueries({ queryKey: rideKeys.mine });
      qc.invalidateQueries({ queryKey: rideKeys.detail(ride.id) });
    },
  });
}

export function useUpdateRide(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRideInput) => ridesApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rideKeys.detail(id) });
      qc.invalidateQueries({ queryKey: rideKeys.mine });
    },
  });
}

export function useDeleteRide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ridesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: rideKeys.mine }),
  });
}

export function useAddMeetingPoint(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MeetingPointInput) => ridesApi.addMeetingPoint(rideId, input),
    onSuccess: (point) => {
      qc.setQueryData<RideDetail>(rideKeys.detail(rideId), (current) =>
        current ? { ...current, meetingPoints: [...current.meetingPoints, point] } : current,
      );
      qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
      qc.invalidateQueries({ queryKey: rideKeys.mine });
    },
  });
}

export function useUpdateMeetingPoint(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pointId, input }: { pointId: string; input: MeetingPointInput }) =>
      ridesApi.updateMeetingPoint(rideId, pointId, input),
    onSuccess: (point) => {
      qc.setQueryData<RideDetail>(rideKeys.detail(rideId), (current) =>
        current
          ? {
              ...current,
              meetingPoints: current.meetingPoints.map((mp) => (mp.id === point.id ? point : mp)),
            }
          : current,
      );
      qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
      qc.invalidateQueries({ queryKey: rideKeys.mine });
    },
  });
}

export function useRemoveMeetingPoint(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pointId: string) => ridesApi.removeMeetingPoint(rideId, pointId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
      qc.invalidateQueries({ queryKey: rideKeys.mine });
    },
  });
}

export function useReorderMeetingPoints(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderedIds }: { orderedIds: string[] }) =>
      ridesApi.reorderMeetingPoints(rideId, orderedIds),
    onSuccess: (points) => {
      qc.setQueryData<RideDetail>(rideKeys.detail(rideId), (current) =>
        current ? { ...current, meetingPoints: points } : current,
      );
      qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
      qc.invalidateQueries({ queryKey: rideKeys.mine });
    },
  });
}
